import { useState } from 'react';
import { getServiceClass } from '../../services/serviceCache';
import type { Requirements } from '../../services/blueprintGeneratorService';
import toast from 'react-hot-toast';

interface RequirementsFormProps {
  projectId: string;
  userId: string;
  onBlueprintGenerated: (blueprintId: string) => void;
}

export function RequirementsForm({ projectId, userId, onBlueprintGenerated }: RequirementsFormProps) {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [requirements, setRequirements] = useState<Requirements>({
    projectName: '',
    description: '',
    features: [''],
    constraints: [''],
    targetAudience: '',
    timeline: '',
    budget: '',
    techStack: {
      frontend: '',
      backend: '',
      database: '',
      infrastructure: '',
    },
  });

  const handleAddFeature = () => {
    setRequirements(prev => ({
      ...prev,
      features: [...prev.features, ''],
    }));
  };

  const handleRemoveFeature = (index: number) => {
    setRequirements(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setRequirements(prev => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  const handleAddConstraint = () => {
    setRequirements(prev => ({
      ...prev,
      constraints: [...(prev.constraints || []), ''],
    }));
  };

  const handleRemoveConstraint = (index: number) => {
    setRequirements(prev => ({
      ...prev,
      constraints: prev.constraints?.filter((_, i) => i !== index),
    }));
  };

  const handleConstraintChange = (index: number, value: string) => {
    setRequirements(prev => ({
      ...prev,
      constraints: prev.constraints?.map((c, i) => (i === index ? value : c)),
    }));
  };

  const handleGenerate = async () => {
    // Validate requirements
    if (!requirements.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    if (!requirements.description.trim()) {
      toast.error('Project description is required');
      return;
    }

    const validFeatures = requirements.features.filter(f => f.trim());
    if (validFeatures.length === 0) {
      toast.error('At least one feature is required');
      return;
    }

    setIsGenerating(true);

    try {
      // Clean up requirements
      const cleanRequirements: Requirements = {
        ...requirements,
        features: validFeatures,
        constraints: requirements.constraints?.filter(c => c.trim()) || [],
      };

      // Generate blueprint
      const BlueprintGeneratorService = await getServiceClass('blueprintGeneratorService', 'BlueprintGeneratorService');
      const blueprint = await BlueprintGeneratorService.generateFromRequirements(
        userId,
        projectId,
        cleanRequirements,
        'anthropic' // Default to Anthropic Claude
      );

      toast.success('Blueprint generated successfully!');
      onBlueprintGenerated(blueprint.id);
    } catch (error) {
      const { logger } = await import('../../services/loggingService');
      logger.error('Failed to generate blueprint', { error, projectId, userId });
      toast.error((error as Error).message || 'Failed to generate blueprint');
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return requirements.projectName.trim() && requirements.description.trim();
      case 2:
        return requirements.features.some(f => f.trim());
      case 3:
        return true; // Tech stack is optional
      case 4:
        return true; // Additional info is optional
      default:
        return false;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    s === step
                      ? 'bg-accent-500 text-primary-foreground'
                      : s < step
                      ? 'bg-success text-success-foreground'
                      : 'bg-subtle text-secondary'
                  }`}
                >
                  {s < step ? '✓' : s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-24 h-1 mx-2 ${
                      s < step ? 'bg-success' : 'bg-background-tertiary'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span className={step === 1 ? 'text-accent-500 font-semibold' : 'text-secondary'}>
              Basic Info
            </span>
            <span className={step === 2 ? 'text-accent-500 font-semibold' : 'text-secondary'}>
              Features
            </span>
            <span className={step === 3 ? 'text-accent-500 font-semibold' : 'text-secondary'}>
              Tech Stack
            </span>
            <span className={step === 4 ? 'text-accent-500 font-semibold' : 'text-secondary'}>
              Additional
            </span>
          </div>
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Project Basic Information
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                value={requirements.projectName}
                onChange={(e) => setRequirements(prev => ({ ...prev, projectName: e.target.value }))}
                className="input-base w-full focus:ring-primary"
                placeholder="e.g., E-commerce Platform"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project Description *
              </label>
              <textarea
                value={requirements.description}
                onChange={(e) => setRequirements(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="input-base w-full focus:ring-primary"
                placeholder="Describe what your project does, who it's for, and what problems it solves..."
              />
            </div>
          </div>
        )}

        {/* Step 2: Features */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Project Features
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              List the key features and functionality your project needs.
            </p>

            <div className="space-y-3">
              {requirements.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="input-base flex-1 focus:ring-primary"
                    placeholder={`Feature ${index + 1}`}
                  />
                  {requirements.features.length > 1 && (
                    <button
                      onClick={() => handleRemoveFeature(index)}
                      className="btn-destructive"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleAddFeature}
              className="btn btn-primary"
            >
              + Add Feature
            </button>
          </div>
        )}

        {/* Step 3: Tech Stack */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Tech Stack Preferences
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Specify your preferred technologies (optional). Leave blank for AI recommendations.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Frontend
                </label>
                <input
                  type="text"
                  value={requirements.techStack?.frontend || ''}
                  onChange={(e) => setRequirements(prev => ({
                    ...prev,
                    techStack: { ...prev.techStack, frontend: e.target.value },
                  }))}
                  className="input-base w-full focus:ring-primary"
                  placeholder="e.g., React, Vue, Angular"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Backend
                </label>
                <input
                  type="text"
                  value={requirements.techStack?.backend || ''}
                  onChange={(e) => setRequirements(prev => ({
                    ...prev,
                    techStack: { ...prev.techStack, backend: e.target.value },
                  }))}
                  className="input-base w-full focus:ring-primary"
                  placeholder="e.g., Node.js, Python, Go"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Database
                </label>
                <input
                  type="text"
                  value={requirements.techStack?.database || ''}
                  onChange={(e) => setRequirements(prev => ({
                    ...prev,
                    techStack: { ...prev.techStack, database: e.target.value },
                  }))}
                  className="input-base w-full focus:ring-primary"
                  placeholder="e.g., PostgreSQL, MongoDB, MySQL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Infrastructure
                </label>
                <input
                  type="text"
                  value={requirements.techStack?.infrastructure || ''}
                  onChange={(e) => setRequirements(prev => ({
                    ...prev,
                    techStack: { ...prev.techStack, infrastructure: e.target.value },
                  }))}
                  className="input-base w-full focus:ring-primary"
                  placeholder="e.g., AWS, Vercel, Docker"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Additional Info */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Additional Information
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Audience
              </label>
              <input
                type="text"
                value={requirements.targetAudience || ''}
                onChange={(e) => setRequirements(prev => ({ ...prev, targetAudience: e.target.value }))}
                className="input-base w-full focus:ring-primary"
                placeholder="e.g., Small businesses, Developers, General consumers"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timeline
              </label>
              <input
                type="text"
                value={requirements.timeline || ''}
                onChange={(e) => setRequirements(prev => ({ ...prev, timeline: e.target.value }))}
                className="input-base w-full focus:ring-primary"
                placeholder="e.g., 3 months, 6 weeks, ASAP"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Budget
              </label>
              <input
                type="text"
                value={requirements.budget || ''}
                onChange={(e) => setRequirements(prev => ({ ...prev, budget: e.target.value }))}
                className="input-base w-full focus:ring-primary"
                placeholder="e.g., £10,000, Limited, Flexible"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Constraints
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Any technical or business constraints to consider
              </p>
              <div className="space-y-3">
                {requirements.constraints?.map((constraint, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={constraint}
                      onChange={(e) => handleConstraintChange(index, e.target.value)}
                      className="input-base flex-1 focus:ring-primary"
                      placeholder={`Constraint ${index + 1}`}
                    />
                    {(requirements.constraints?.length || 0) > 1 && (
                      <button
                        onClick={() => handleRemoveConstraint(index)}
                        className="btn-destructive"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={handleAddConstraint}
                className="btn btn-primary mt-3"
              >
                + Add Constraint
              </button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setStep(prev => Math.max(1, prev - 1))}
            disabled={step === 1}
            className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !canProceed()}
              className="btn-success disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating Blueprint...
                </>
              ) : (
                'Generate Blueprint'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
